import { shallowReactive } from 'vue'
import { defineStore } from 'pinia'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import { useToast } from '@bv/composables/common/toast'
import { userDetailsStore } from '@/stores/auth/user-details.store'
import AgentAvailability from '@/api/reports/agentAvailability.api'
import CallerInfoApi from '@/api/webphone/callerInfo.api'
import { i18n } from '@/helpers/locale/locale.helper'
import { hasActiveFilter as isFilterActive } from '@/helpers/live-panel/filter-extensions'

const toast = useToast()

export const livePanelStore = defineStore('livePanel', {
  state: () => ({
    _serverTimeDiff: 0,
    _reactive: shallowReactive({
      items: {
        extensions: { value: {}, pendingUpdates: 0, isFetching: true },
        presences: { value: {}, pendingUpdates: 0, isFetching: true },
        queues: { value: {}, pendingUpdates: 0, isFetching: true },
        agentsQueues: { value: {}, pendingUpdates: 0, isFetching: true },
        teams: { value: {}, pendingUpdates: 0, isFetching: true },
        extensionsCalls: { value: {}, pendingUpdates: 0, isFetching: true }
      }
    }),
    _intervalUpdateReactive: null,
    _waitingCalls: {},
    _calls: {},
    _dashboardStats: {},
    _agentStats: {},
    _availableAgents: [],
    _callsInfo: {},
    _search: '',
    _grouping: {
      by: localStorage.getItem('live-panel-grouping-by') || 'extensions' // extensions | queues | teams
    },
    _sorting: {
      by: 'ascending_a_z' // null | ascending_a_z | descending_z_a | duration_in_state_up | duration_in_state_down
    },
    _filter: {},
    _uiFlags: {
      isExtensionsInitialized: false,
      isFetchingAvailableAgents: true,
      isFetchingCalls: true
    }
  }),
  getters: {
    extensions(state) {
      return state._reactive.items.extensions.value
    },
    queues(state) {
      return state._reactive.items.queues.value
    },
    waitingCalls(state) {
      return state._waitingCalls
    },
    calls(state) {
      return state._calls
    },
    serverTimeDiff(state) {
      return state._serverTimeDiff
    },
    currentUserExtension(state) {
      const authStore = userDetailsStore()
      const extension = authStore.currentExtension.number
      return extension ? (state._reactive.items.extensions.value?.[extension] ?? {}) : {}
    },
    currentUserPresence(state) {
      const authStore = userDetailsStore()
      const extension = authStore.currentExtension.number
      return extension ? (state._reactive.items.presences.value[extension] ?? {}) : {}
    },
    queuesArray(state) {
      return Object.values(state._reactive.items.queues.value)
    },
    queuesAgents(state) {
      return state.queuesArray
        .map((queue) =>
          Object.values(queue.agents).map((agent) => ({
            agentNumber: agent.number,
            queueNumber: queue.number,
            agent: agent
          }))
        )
        .flat()
    },
    teams(state) {
      return state._reactive.items.teams.value
    },
    extensionsCalls(state) {
      return state._reactive.items.extensionsCalls.value
    },
    currentUserCalls(state) {
      const extension = state.currentUserExtension.number
      return extension ? state._reactive.items.extensionsCalls.value[extension] || {} : {}
    },
    uiFlags(state) {
      return {
        isFetchingAvailableAgents: state._uiFlags.isFetchingAvailableAgents,
        isFetchingExtensions: state._reactive.items.extensions.isFetching,
        isFetchingPresences: state._reactive.items.presences.isFetching,
        isFetchingQueues: state._reactive.items.queues.isFetching,
        isFetchingTeams: state._reactive.items.teams.isFetching,
        isFetchingCalls: state._uiFlags.isFetchingCalls,
        isFetching:
          Object.values(state._uiFlags).filter((isFetching) => isFetching).length +
          Object.values(state._reactive.items).filter((item) => item.isFetching).length,
        isFetchingLivePanel:
          state._reactive.items.extensions.isFetching ||
          state._reactive.items.presences.isFetching ||
          state._reactive.items.queues.isFetching ||
          state._reactive.items.teams.isFetching ||
          state._uiFlags.isFetchingCalls
      }
    },
    grouping(state) {
      return state._grouping
    },
    sorting(state) {
      return state._sorting
    },
    search(state) {
      return state._search
    },
    filter(state) {
      return state._filter
    },
    hasActiveFilter(state) {
      return isFilterActive(state._filter)
    },
    availableAgents(state) {
      return state._availableAgents
    },
    availableExtensions(state) {
      return Object.values(state._reactive.items.extensions.value).filter(
        (extension) =>
          ['idle', 'available'].includes(extension.status?.toLowerCase()) &&
          state._reactive.items.presences.value[extension.number]?.status === 'available'
      )
    }
  },
  actions: {
    pushToReactive(itemName) {
      this._reactive.items[itemName].pendingUpdates++
    },
    startLivePanelReactive() {
      this._startReactive()
    },
    startDashboardReactive() {
      this._startReactive(['queues'])
    },
    startWebPhoneReactive() {
      this._startReactive(['extensions'])
    },
    stopReactive() {
      this._intervalUpdateReactive = clearInterval(this._intervalUpdateReactive)
      this._intervalUpdateReactive = null
    },
    updateReactive(keys = undefined) {
      const entriesToUpdate = Object.entries(this._reactive.items).filter(
        ([key, item]) => (!keys || keys.includes(key)) && item.pendingUpdates
      )

      if (entriesToUpdate.length) {
        entriesToUpdate.forEach(([_, item]) => {
          item.value = { ...item.value }
          item.pendingUpdates = 0
          if (item.isFetching) {
            item.isFetching = false
          }
        })

        this._reactive.items = { ...this._reactive.items }
      }
    },
    getAgentCalls(extension) {
      return this._reactive.items.extensionsCalls.value[extension]
    },
    getQueueWaitingCalls(queueNumber) {
      return this._waitingCalls[queueNumber]
    },
    getAgentQueues(extension) {
      return this._reactive.items.agentsQueues.value[extension] || {}
    },
    getAgentPresence(extension) {
      return this._reactive.items.presences.value[extension]
    },
    initExtensions(extensions) {
      const agents = {}
      const presences = {}

      Object.values(extensions).forEach((agent) => {
        presences[agent.number] = agent.presence

        agents[agent.number] = {
          number: agent.number,
          name: agent.name,
          status: agent.statusText?.toLowerCase() || ''
        }

        if (!this._reactive.items.extensionsCalls.value[agent.number]) {
          this._reactive.items.extensionsCalls.value[agent.number] = {}
        }
      })

      this._reactive.items.extensions.value = agents
      this.pushToReactive('extensions')

      this._reactive.items.presences.value = presences
      this.pushToReactive('presences')

      this.pushToReactive('extensionsCalls')

      if (!this._uiFlags.isExtensionsInitialized) {
        this._uiFlags.isExtensionsInitialized = true
        this.updateReactive(['extensions', 'presences', 'extensionsCalls'])
      }
    },
    updateWaitingCalls(waitingCalls) {
      waitingCalls.notify = +waitingCalls.waitingCallsCount > 0
      this._waitingCalls[waitingCalls.queueNumber] = waitingCalls

      if (!this._reactive.items.queues.value[waitingCalls.queueNumber]) {
        this._reactive.items.queues.value[waitingCalls.queueNumber] = {}
      }
      this._reactive.items.queues.value[waitingCalls.queueNumber].waitingCallsCount =
        waitingCalls.waitingCallsCount
      this._reactive.items.queues.value[waitingCalls.queueNumber].waitingCalls =
        waitingCalls.waitingCalls
      this.pushToReactive('queues')
    },
    updateServerTimeDiff({ timestamp }) {
      this._serverTimeDiff = moment.now() * 1 - timestamp
    },
    async getAvailableAgents() {
      AgentAvailability.get({}, 'summary')
        .then(async (response) => {
          this._availableAgents = response.data.data
          this._uiFlags.isFetchingAvailableAgents = false
        })
        .catch((e) => {
          throw new Error(e)
        })
    },
    updateAvailableAgents(updatedAgent) {
      let agentInList = this._availableAgents.find(
        (agent) => agent.agentNumber === updatedAgent.agentNumber
      )
      if (agentInList) {
        agentInList = {
          ...agentInList,
          ...updatedAgent
        }
      } else {
        this._availableAgents.push(updatedAgent)
      }
    },
    setGrouping(grouping) {
      localStorage.setItem('live-panel-grouping-by', grouping.by)
      this._grouping = grouping
    },
    setSorting(sorting) {
      this._sorting = sorting
    },
    setSearch(search) {
      this._search = (search ?? '').trim()
    },
    setFilter(filter) {
      this._filter = cloneDeep(filter)
    },
    updateDashboardStats(stats) {
      this._dashboardStats[stats.queueNumber] = {
        ...(this._dashboardStats[stats.queueNumber] || {}),
        ...stats
      }
    },
    updateAgentStats(stats) {
      this._agentStats = { ...this._agentStats, ...stats }
    },
    updateQueueWaitingCalls(stats) {
      this._waitingCalls[stats.queueNumber] = stats
    },
    markWaitingCallsAsNotified(queueNumber) {
      if (this._waitingCalls[queueNumber]) {
        this._waitingCalls[queueNumber].notify = false
      }
    },
    removeFromQueue(extensionNumber, queue) {
      delete this._reactive.items.agentsQueues.value[extensionNumber]?.[queue]
      delete this._reactive.items.queues.value[queue]?.[extensionNumber]
    },
    updateExtension(extension) {
      const status = extension.statusText?.toLowerCase() || ''

      this._reactive.items.extensions.value[extension.number] = {
        number: extension.number,
        name: extension.name,
        status
      }

      this.pushToReactive('extensions')

      if (extension.number === this.currentUserExtension.number) {
        this.updateReactive(['extensions'])
      }
    },
    updateExtensionPresence(presence) {
      const extensionNumber = presence.number
      delete presence.number
      this._reactive.items.presences.value[extensionNumber] = presence

      this.pushToReactive('presences')

      if (extensionNumber === this.currentUserExtension.number) {
        this.updateReactive(['presences'])
      }
    },
    initQueues(queues) {
      this._reactive.items.queues.value = queues
      this.pushToReactive('queues')

      this.queuesArray.forEach((queue) => {
        if (+queue.waitingCallsCount > 0) {
          this._waitingCalls[queue.number] = {
            queueName: queue.name,
            queueNumber: queue.number,
            waitingCallsCount: queue.waitingCallsCount,
            waitingCalls: queue.waitingCalls
          }
        }
      })

      const agentsQueuesRelation = Object.groupBy(
        Object.values(queues)
          .map((queue) =>
            Object.values(queue.agents).map((agent) => ({
              ...agent,
              queueNumber: queue.number,
              queueName: queue.name
            }))
          )
          .flat(),
        (agent) => agent.number
      )

      const agentsQueues = {}
      for (let agentNumber in agentsQueuesRelation) {
        agentsQueuesRelation[agentNumber].forEach((agent) => {
          agentsQueues[agentNumber] ??= {}
          agentsQueues[agentNumber][agent.queueNumber] = agent
        })
      }
      this._reactive.items.agentsQueues.value = agentsQueues
      this.pushToReactive('agentsQueues')
    },
    updateQueueMember(agent) {
      if (!this._reactive.items.agentsQueues.value[agent.number]) {
        this._reactive.items.agentsQueues.value[agent.number] = {}
      }
      this._reactive.items.agentsQueues.value[agent.number][agent.queueNumber] = agent
      this.pushToReactive('agentsQueues')

      if (!this._reactive.items.queues.value[agent.queueNumber]) {
        this._reactive.items.queues.value[agent.queueNumber] = { agents: {} }
      }
      this._reactive.items.queues.value[agent.queueNumber].agents[agent.number] = agent
      this.pushToReactive('queues')
    },
    addDynamicMember(extensionNumber, queueNumber) {
      const newAgent = {
        isPaused: false,
        membershipType: 'dynamic',
        name: this._reactive.items.extensions.value[extensionNumber].name,
        number: extensionNumber,
        pausedReason: '',
        status: this._reactive.items.extensions.value[extensionNumber].status
      }

      if (this._reactive.items.queues.value[queueNumber]?.agents) {
        this._reactive.items.queues.value[queueNumber].agents[extensionNumber] = newAgent
        this.pushToReactive('queues')
      }

      if (!this._reactive.items.agentsQueues.value[extensionNumber]) {
        this._reactive.items.agentsQueues.value[extensionNumber] = {}
      }
      this._reactive.items.agentsQueues.value[extensionNumber][queueNumber] = newAgent
      this.pushToReactive('agentsQueues')
    },
    removeDynamicMember(extensionNumber, queueNumber) {
      if (
        this.this._reactive.items.queues.value[queueNumber]?.agents[extensionNumber]
          ?.membershipType === 'dynamic'
      ) {
        delete this._reactive.items.queues.value[queueNumber]?.agents[extensionNumber]
        this.pushToReactive('queues')
      }
    },
    initTeams(teams) {
      this._reactive.items.teams.value = teams
      this.pushToReactive('teams')
    },
    initCalls(calls) {
      Object.values(calls).forEach((call) => {
        this.addCall(call, { trackCall: false })
      })

      const callsChannels = Object.values(calls).map((call) => call.channel)
      Object.values(this._calls).forEach((call) => {
        if (!callsChannels.includes(call.channel)) {
          this.removeCall(call)
        }
      })

      if (this._uiFlags.isFetchingCalls) {
        this._uiFlags.isFetchingCalls = false
      }
    },
    addCall(call, { trackCall = true } = {}) {
      if (trackCall) {
        this._trackCallToShowInfo(call)
      }

      if (call.status === 'Down') {
        return
      }

      this._calls[call.channel] = call

      if (!this._reactive.items.extensionsCalls.value[call.extension]) {
        this._reactive.items.extensionsCalls.value[call.extension] = {}
      }
      this._reactive.items.extensionsCalls.value[call.extension][call.channel] = call
      this.pushToReactive('extensionsCalls')
    },
    updateCall(call) {
      if (call.status === 'Down') {
        return
      }

      this._calls[call.channel] = call

      if (!this._reactive.items.extensionsCalls.value[call.extension]) {
        this._reactive.items.extensionsCalls.value[call.extension] = {}
      }
      this._reactive.items.extensionsCalls.value[call.extension][call.channel] = call

      if (this._callsInfo[call.extension] && this._callsInfo[call.extension][call.channel]) {
        this._callsInfo[call.extension][call.channel].status = call.status
        this._callsInfo[call.extension][call.channel].connectedLineNum = call.connectedLineNum
      }
      this.pushToReactive('extensionsCalls')
    },
    removeCall(call) {
      delete this._calls[call.channel]
      delete this._reactive.items.extensionsCalls.value[call.extension]?.[call.channel]
      this.pushToReactive('extensionsCalls')
    },

    _startReactive(keys = undefined, interval = 1000) {
      this.updateReactive(keys)
      this._intervalUpdateReactive = setInterval(() => {
        this.updateReactive(keys)
      }, interval)
    },

    /**
     * Watch call to show popup when recieve all data.
     *
     * @param {object} call - Call object.
     * @returns {void}
     */
    _trackCallToShowInfo(call) {
      if (
        call.extension == userDetailsStore().currentExtension.number && // is current extension
        userDetailsStore().userWorkspaceServiceDetails.settings.call_http_request && // is feature enabled
        call.direction.startsWith('inbound') // is incomming call
      ) {
        this._callsInfo[call.extension] ??= {}
        this._callsInfo[call.extension][call.channel] = {
          _number: call.connectedLineNum,
          status: call.status,
          direction: call.direction
        }

        const that = this
        Object.defineProperty(this._callsInfo[call.extension][call.channel], 'connectedLineNum', {
          get() {
            return this._connectedLineNum
          },
          set(value) {
            if (that._isListenOrListenAndWhisper(value)) {
              delete that._callsInfo[call.extension]?.[call.channel]
            } else if (value && this.status !== 'Down' && this.direction.startsWith('inbound')) {
              that._openCallerInfoPopup(value)
              delete that._callsInfo[call.extension]?.[call.channel]
            }
            this._connectedLineNum = value
          }
        })
      }
    },

    /**
     * Get caller info and show popup.
     *
     * @param {number} number - Caller number.
     * @returns {void}
     */
    _openCallerInfoPopup(number) {
      CallerInfoApi.getCallerInfo(number).then((data) => {
        if (data.data) {
          this._renderPopUp(data.data)
        }
      })
    },
    /**
     * Render content in popup.
     *
     * @param {string} content - HTML content.
     * @returns {void}
     */
    _renderPopUp(content) {
      const trimContent = content.trim()
      // Check if content is a string and starts with a valid URL or HTML
      if (
        typeof content !== 'string' ||
        (!trimContent.startsWith('http') &&
          !trimContent.startsWith('<html>') &&
          !trimContent.startsWith('<!DOCTYPE html>'))
      ) {
        toast.error(i18n.global.t('call_center.webphone.wrong_response'))
        return
      }

      let popup = null

      // Open URL in a new window if it's a valid link
      if (trimContent.startsWith('http')) {
        popup = window.open(content, '_blank', 'width=500,height=500')
      }

      // Open HTML content in a new window if it's HTML
      if (trimContent.startsWith('<html>') || trimContent.startsWith('<!DOCTYPE html>')) {
        popup = window.open('', '_blank', 'width=500,height=500')

        if (popup) {
          popup.document.open()
          popup.document.write(content)
          popup.document.close()
        }
      }

      // Check if the popup was blocked
      if (!popup) {
        toast.error(i18n.global.t('call_center.webphone.popup_blocked'))
      }
    },
    /**
     * Check call is listen or listen & whisper.
     *
     * @param {string} number - Number of caller.
     * @returns {boolean}
     */
    _isListenOrListenAndWhisper(number) {
      return (
        `${number}`.toLowerCase().startsWith('listen|') ||
        `${number}`.toLowerCase().startsWith('listen&whisper|')
      )
    }
  }
})
